// CustomTicketRow.tsx
import React from "react";
import { FormattedMessage } from "react-intl";
import { TicketRow } from "@hooplab/ui-components";
import formatCurrency from "../../helpers/formatCurrency";
import { newPriceMode } from "../../helpers/priceMode";
import { TicketTypeCartItem, PriceMode } from "../../types";
import FormattedDateHelper from "../../screens/Complete/FormattedDateHelper";
import { AvailabilityState, SelectedState } from "@hooplab/ui-components/dist/types";
import { useCurrentLangContext } from "../../hooks/useCurrentLang";

interface CustomTicketRowProps {
  ticketType: TicketTypeCartItem;
  eventStart: Date;
  eventEnd: Date;
  priceMode: PriceMode;
  quantities: { [ticketTypeId: number]: number };
  higherOrderAvailability: AvailabilityState;
  onChangeQuantity: (ticketType: TicketTypeCartItem, quantity: number) => void;
  error: React.ReactNode | null;
  exceedLimit: { error: boolean; ids: number[] };
  lessThanMin: { error: boolean; ids: number[] };
  divisibleBy: { error: boolean; ids: number[] };
}

const determineTicketState = (ticketType: TicketTypeCartItem): SelectedState => {
  if (ticketType.isSecret) {
    return "PROMO";
  }
  if (ticketType.isFeatured) {
    return "FEATURED";
  }
  return "NONE";
};

const getValidity = (
  ticketType: TicketTypeCartItem,
  eventStart: Date,
  eventEnd: Date,
): { validFrom: Date; validTo: Date } => {
  const { isFullDay, validFrom, validTo } = ticketType;
  if (isFullDay) {
    if (validFrom) {
      return { validFrom, validTo: validFrom };
    } else if (validTo) {
      return { validFrom: validTo, validTo };
    } else {
      return { validFrom: validFrom || eventStart, validTo: validTo || eventEnd };
    }
  } else {
    return { validFrom: validFrom || eventStart, validTo: validTo || eventEnd };
  }
};

const FeeToolTip: React.FC<{
  ticketType: TicketTypeCartItem;
  priceMode: PriceMode;
  shouldBeHoverable: boolean;
}> = ({ ticketType, priceMode, shouldBeHoverable }) => {
  const {
    provided: {
      currentLocale: { name: localeName },
    },
  } = useCurrentLangContext()!;
  return (
    <li>
      <span className={"text-sm mt-1"}>
        <FormattedMessage
          id="components.Cart.TicketTypeRow.specifiedFee"
          defaultMessage={"Where {amount} is <u>fees</u>"}
          values={{
            amount: (
              <span>
                {formatCurrency(
                  (newPriceMode(priceMode, ticketType.price, ticketType.fee, ticketType.vatFactor)
                    .fee +
                    ticketType.requiredAddonProducts.reduce(
                      (acc, ap) =>
                        acc +
                        newPriceMode(priceMode, ap.price, ap.fee, ap.vatFactor).fee *
                          (ap.required || 0),
                      0,
                    )) /
                    100,
                  localeName,
                )}
              </span>
            ),
            u: (chunks) =>
              shouldBeHoverable ? (
                <div className="relative group inline-block">
                  <span className="underline">{chunks} </span>
                  <div className="absolute hidden group-hover:block group-focus:block rounded-lg w-fit z-30 min-w-60 bg-white text-gray-700 border border-gray-100 shadow-md -right-28 px-3 py-2">
                    <div className="font-semibold text-black mb-1">
                      <FormattedMessage
                        id="components.Cart.TicketTypeRow.fees"
                        defaultMessage={"Fees"}
                      />
                    </div>
                    <div className="grid grid-cols-3 odd:[&>div]:col-span-2 w-full even:[&>div]:text-right odd:[&>div]:pl-2 even:[&>div]:pr-2 [&>div]:py-1">
                      <div className="bg-gray-100">{ticketType.name}</div>
                      <div className="bg-gray-100">
                        {formatCurrency(
                          newPriceMode(
                            priceMode,
                            ticketType.price,
                            ticketType.fee,
                            ticketType.vatFactor,
                          ).fee / 100,
                          localeName,
                        )}
                      </div>
                      {ticketType.requiredAddonProducts.map((ap) => (
                        <React.Fragment>
                          <div>{ap.name}</div>
                          <div>
                            {formatCurrency(
                              newPriceMode(priceMode, ap.price, ap.fee, ap.vatFactor).fee / 100,
                              localeName,
                            )}
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <span>{chunks} </span>
              ),
          }}
        />
      </span>
    </li>
  );
};

const determineTicketTypeAvailability = (
  ticketTypeAvailability: AvailabilityState,
  higherOrderAvailability: AvailabilityState,
) => {
  if (
    (ticketTypeAvailability === "FEW_LEFT" || ticketTypeAvailability === "NO_RESERVABLE") &&
    (higherOrderAvailability === "FEW_LEFT" || higherOrderAvailability === "NO_RESERVABLE")
  ) {
    return "MANY_LEFT";
  }
  return ticketTypeAvailability;
};

const CustomTicketRow: React.FC<CustomTicketRowProps> = ({
  ticketType,
  eventStart,
  eventEnd,
  priceMode,
  quantities,
  onChangeQuantity,
  higherOrderAvailability,
  error,
  exceedLimit,
  lessThanMin,
  divisibleBy,
}) => {
  const {
    provided: {
      currentLocale: { name: localeName },
    },
  } = useCurrentLangContext()!;

  const { validFrom, validTo } = getValidity(ticketType, eventStart, eventEnd);
  const totalFee =
    ticketType.fee + ticketType.requiredAddonProducts.reduce((acc, ap) => acc + ap.fee, 0);

  return (
    <TicketRow
      key={ticketType.ticketTypeId}
      name={ticketType.name}
      formattedPrice={formatCurrency(
        (newPriceMode(priceMode, ticketType.price, ticketType.fee, ticketType.vatFactor).price +
          ticketType.requiredAddonProducts.reduce(
            (acc, ap) =>
              acc +
              newPriceMode(priceMode, ap.price, ap.fee, ap.vatFactor).price * (ap.required || 0),
            0,
          )) /
          100 +
          (newPriceMode(priceMode, ticketType.price, ticketType.fee, ticketType.vatFactor).fee +
            ticketType.requiredAddonProducts.reduce(
              (acc, ap) =>
                acc +
                newPriceMode(priceMode, ap.price, ap.fee, ap.vatFactor).fee * (ap.required || 0),
              0,
            )) /
            100,
        localeName,
      )}
      description={ticketType.description || ""}
      quantity={quantities[ticketType.ticketTypeId] || 0}
      selected={determineTicketState(ticketType)}
      availability={determineTicketTypeAvailability(
        ticketType.availabilityStatus,
        higherOrderAvailability,
      )}
      onChangeQuantity={(q: any) => onChangeQuantity(ticketType, q)}
      fewLeftMessage={
        <FormattedMessage
          tagName={"span"}
          id="components.Cart.TicketTypeRow.fewLeftLabel"
          defaultMessage={"Few left"}
        />
      }
      soldOutMessage={
        <FormattedMessage
          tagName={"span"}
          id="components.Cart.TicketTypeRow.soldOut"
          defaultMessage={"Sold out!"}
        />
      }
      showMoreMessage={
        <FormattedMessage
          id="components.Cart.TicketTypeRow.showMore"
          defaultMessage={"Show more"}
        />
      }
      showDetailsMessage={{
        show: (
          <FormattedMessage
            id="components.Cart.TicketTypeRow.showDetails"
            defaultMessage={"Show details"}
          />
        ),
        hide: (
          <FormattedMessage
            id="components.Cart.TicketTypeRow.hideDetails"
            defaultMessage={"Hide details"}
          />
        ),
      }}
      tooltipText={
        <span className="line-clamp-2">
          <FormattedMessage
            tagName={"span"}
            id="components.Cart.TicketTypeRow.validTicket"
            defaultMessage={"This ticket applies to "}
          />
          <FormattedDateHelper
            dateFormat="start_end_date_and_time"
            start={validFrom}
            end={validTo}
            text=""
            includeClockOnEqual={!ticketType.isFullDay}
          />
        </span>
      }
      tooltipActive={
        ticketType.availabilityStatus !== "SOLD_OUT" &&
        (!!ticketType.validFrom || !!ticketType.validTo)
      }
      error={!!error}
      errorMessage={<div className={"mt-2 text-sm text-red-700/90"}>{error}</div>}
      showFeeSpecification={priceMode.includes("SPECIFIED_FEE") && totalFee > 0}
      feeSpecification={
        <ol className={"flex flex-col mt-0.5 list-disc ml-5 [&>li]:marker:text-gray-500"}>
          <FeeToolTip
            priceMode={priceMode}
            ticketType={ticketType}
            shouldBeHoverable={
              ticketType.requiredAddonProducts.length > 0 &&
              ticketType.availabilityStatus !== "SOLD_OUT"
            }
          />
        </ol>
      }
      requiredAddonsContent={
        <ol className={"flex flex-col mt-0.5 list-disc ml-5 [&>li]:marker:text-gray-500"}>
          {ticketType.requiredAddonProducts.map((ap, index) => (
            <li key={index}>
              <span className={"text-sm mt-1"}>
                <FormattedMessage
                  id="components.Cart.TicketTypeRow.includedAddons"
                  defaultMessage={
                    "Includes {products} {hidePrice, select, true {} false {to a value of {price}} other {}}"
                  }
                  values={{
                    hidePrice: ap.hidePrice,
                    products: ap.minPerTicket !== 1 ? `${ap.minPerTicket} ${ap.name}` : ap.name,
                    price: formatCurrency(
                      (newPriceMode(priceMode, ap.price, ap.fee, ap.vatFactor).price *
                        (ap.minPerTicket || 0)) /
                        100 +
                        (newPriceMode(priceMode, ap.price, ap.fee, ap.vatFactor).fee *
                          (ap.minPerTicket || 0)) /
                          100,
                      localeName,
                    ),
                  }}
                />
              </span>
            </li>
          ))}
        </ol>
      }
      requiredAddons={
        ticketType.requiredAddonProducts.length > 0 ||
        (priceMode.includes("SPECIFIED_FEE") && ticketType.fee > 0)
      }
      exceedsLimit={exceedLimit && exceedLimit.ids.includes(ticketType.ticketTypeId) ? true : false}
      lessThanMin={lessThanMin && lessThanMin.ids.includes(ticketType.ticketTypeId) ? true : false}
      divisibleBy={divisibleBy && divisibleBy.ids.includes(ticketType.ticketTypeId) ? true : false}
      exceedsLimitMessage={
        <div className={"mt-2 text-sm text-red-700/90"}>
          <FormattedMessage
            id="components.Cart.TicketTypeRow.capacityExceeded"
            defaultMessage={
              "You may not buy more than {n} tickets of this ticket category per purchase."
            }
            values={{ n: ticketType.countRule.max, category: ticketType.name }}
          />
        </div>
      }
      lessThanMinMessage={
        <div className={"mt-2 text-sm text-red-700/90"}>
          <FormattedMessage
            id="components.Cart.TicketTypeRow.notEnoughInCart"
            defaultMessage={"You must buy a minimum of {n} tickets of this ticket category."}
            values={{ n: ticketType.countRule.min, category: ticketType.name }}
          />
        </div>
      }
      divisibleByMessage={
        <div className={"mt-2 text-sm text-red-700/90"}>
          <FormattedMessage
            id="components.Cart.TicketTypeRow.divisibleBy"
            defaultMessage={"The number of tickets must be divisible by {n}."}
            values={{ n: ticketType.countRule.mod }}
          />
        </div>
      }
    />
  );
};

export default CustomTicketRow;
